import * as React from "react";
import { cn } from "../lib/cn";

export type MessageMetaStatus = "sending" | "sent" | "streaming" | "error";

export interface MessageMetaProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Sender label (e.g. `"Ando"`, `"Andrea"`). User messages typically hide this. */
  author?: React.ReactNode;
  /** Pre-formatted timestamp (e.g. `"14:30"`). Caller owns locale + format. */
  timestamp?: React.ReactNode;
  /**
   * Right-aligns the row (used by user-role messages). The v18 mockup
   * mirrors meta + bubble against the right edge.
   */
  align?: "start" | "end";
  /**
   * Drives the small dot indicator. `sent` shows the brand orange dot,
   * `streaming` pulses, `error` flips it to destructive, `sending` hides it.
   */
  status?: MessageMetaStatus;
}

/**
 * MessageMeta — uppercase tracking row above the bubble.
 *
 * Maps 1:1 to `.msg-meta` in `tasks/mockups/ando_v18.html` (lines 667-676).
 * Spans:
 *   • `.msg-dot` — orange brand dot (4px, only on agent messages).
 *   • `Ando · 14:30` — author + timestamp, ALL-CAPS tracking 0.1em.
 *
 * Scaffolding only — wire-up (live time, status) happens inside
 * `apps/client/src/features/chat`.
 */
export const MessageMeta = React.forwardRef<HTMLDivElement, MessageMetaProps>(
  ({ author, timestamp, align = "start", status, className, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex items-center gap-2 font-medium text-[10px] text-ink-2 uppercase tracking-[0.1em]",
        align === "end" ? "justify-end" : "justify-start",
        className,
      )}
      {...rest}
    >
      {status !== "sending" && status !== undefined ? (
        <span
          aria-hidden
          className={cn(
            "inline-block h-1 w-1 shrink-0 rounded-full",
            status === "error" && "bg-destructive",
            status === "streaming" && "animate-ai-pulse bg-ando-orange",
            status === "sent" && "bg-ando-orange",
          )}
        />
      ) : null}
      {author ? <span>{author}</span> : null}
      {author && timestamp ? <span aria-hidden>·</span> : null}
      {timestamp ? <span>{timestamp}</span> : null}
    </div>
  ),
);
MessageMeta.displayName = "MessageMeta";
